import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function InvoiceDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data - in a real app, this would be fetched from an API
  const invoice: Invoice = {
    id: id || '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+94 77 123 4567',
    amount: 125000,
    currency: 'Rs.',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    notes: 'Thank you for your business!',
    items: [{
      id: '1',
      description: 'Premium Plan Subscription',
      quantity: 1,
      unitPrice: 100000,
      total: 100000
    }, {
      id: '2',
      description: 'Additional User Licenses',
      quantity: 5,
      unitPrice: 5000,
      total: 25000
    }],
    subtotal: 125000,
    taxAmount: 0,
    discountAmount: 0,
    total: 125000
  };
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-4335">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-6" data-id="element-4336">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4337">
        <button onClick={() => navigate('/invoices')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4338">
          <ArrowLeftIcon size={20} data-id="element-4339" />
          Back to Invoices
        </button>
        <div className="flex items-center gap-3" data-id="element-4340">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4341">
            <PrinterIcon size={18} data-id="element-4342" />
            Print
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4343">
            <DownloadIcon size={18} data-id="element-4344" />
            Download PDF
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4345">
            <MailIcon size={18} data-id="element-4346" />
            Send Email
          </button>
          <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4347">
            <EditIcon size={18} data-id="element-4348" />
            Edit
          </button>
        </div>
      </div>
      {/* Invoice Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-4349">
        {/* Invoice Header */}
        <div className="flex justify-between items-start mb-8" data-id="element-4350">
          <div data-id="element-4351">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4352">INVOICE</h1>
            <p className="text-lg text-gray-600" data-id="element-4353">{invoice.invoiceNumber}</p>
          </div>
          <div className="text-right" data-id="element-4354">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/c3ZBwzojuSYrA5qxKsGMb1/logo_geniebusiness.svg" alt="Genie Business" className="h-12 mb-4" data-id="element-4355" />
            <p className="text-sm text-gray-600" data-id="element-4356">Genie Business</p>
            <p className="text-sm text-gray-600" data-id="element-4357">Colombo, Sri Lanka</p>
          </div>
        </div>
        {/* Status and Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-200" data-id="element-4358">
          <div data-id="element-4359">
            <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4360">Bill To:</h3>
            <p className="text-lg font-semibold text-gray-900" data-id="element-4361">
              {invoice.customerName}
            </p>
            <p className="text-sm text-gray-600" data-id="element-4362">{invoice.customerEmail}</p>
            {invoice.customerPhone && <p className="text-sm text-gray-600" data-id="element-4363">{invoice.customerPhone}</p>}
          </div>
          <div className="text-right" data-id="element-4364">
            <div className="mb-4" data-id="element-4365">{getStatusBadge(invoice.status)}</div>
            <div className="space-y-2" data-id="element-4366">
              <div className="flex justify-end gap-4" data-id="element-4367">
                <span className="text-sm text-gray-500" data-id="element-4368">Issue Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4369">
                  {new Date(invoice.issueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              <div className="flex justify-end gap-4" data-id="element-4370">
                <span className="text-sm text-gray-500" data-id="element-4371">Due Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4372">
                  {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              {invoice.paidDate && <div className="flex justify-end gap-4" data-id="element-4373">
                  <span className="text-sm text-gray-500" data-id="element-4374">Paid Date:</span>
                  <span className="text-sm font-medium text-green-600" data-id="element-4375">
                    {new Date(invoice.paidDate).toLocaleDateString('en-GB')}
                  </span>
                </div>}
            </div>
          </div>
        </div>
        {/* Invoice Items */}
        <div className="mb-8" data-id="element-4376">
          <table className="min-w-full" data-id="element-4377">
            <thead data-id="element-4378">
              <tr className="border-b border-gray-200" data-id="element-4379">
                <th className="py-3 text-left text-sm font-medium text-gray-500" data-id="element-4380">
                  Description
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4381">
                  Quantity
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4382">
                  Unit Price
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-4383">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4384">
              {invoice.items.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-4385">
                  <td className="py-4 text-sm text-gray-900" data-id="element-4386">
                    {item.description}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-4387">
                    {item.quantity}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-4388">
                    Rs. {item.unitPrice.toLocaleString('en-LK')}
                  </td>
                  <td className="py-4 text-sm font-medium text-gray-900 text-right" data-id="element-4389">
                    Rs. {item.total.toLocaleString('en-LK')}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Totals */}
        <div className="flex justify-end mb-8" data-id="element-4390">
          <div className="w-full max-w-sm space-y-2" data-id="element-4391">
            <div className="flex justify-between py-2" data-id="element-4392">
              <span className="text-sm text-gray-600" data-id="element-4393">Subtotal:</span>
              <span className="text-sm font-medium text-gray-900" data-id="element-4394">
                Rs. {invoice.subtotal.toLocaleString('en-LK')}
              </span>
            </div>
            {invoice.discountAmount && invoice.discountAmount > 0 && <div className="flex justify-between py-2" data-id="element-4395">
                <span className="text-sm text-gray-600" data-id="element-4396">Discount:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4397">
                  - Rs. {invoice.discountAmount.toLocaleString('en-LK')}
                </span>
              </div>}
            {invoice.taxAmount && invoice.taxAmount > 0 && <div className="flex justify-between py-2" data-id="element-4398">
                <span className="text-sm text-gray-600" data-id="element-4399">Tax:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-4400">
                  Rs. {invoice.taxAmount.toLocaleString('en-LK')}
                </span>
              </div>}
            <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-4401">
              <span className="text-lg font-semibold text-gray-900" data-id="element-4402">
                Total:
              </span>
              <span className="text-lg font-bold text-gray-900" data-id="element-4403">
                Rs. {invoice.total.toLocaleString('en-LK')}
              </span>
            </div>
          </div>
        </div>
        {/* Notes */}
        {invoice.notes && <div className="pt-6 border-t border-gray-200" data-id="element-4404">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-4405">Notes:</h3>
            <p className="text-sm text-gray-600" data-id="element-4406">{invoice.notes}</p>
          </div>}
      </div>
      {/* Action Buttons */}
      {invoice.status === 'sent' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4407">
          <div className="flex items-center justify-between" data-id="element-4408">
            <div data-id="element-4409">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-4410">
                Mark as Paid
              </h3>
              <p className="text-sm text-gray-600" data-id="element-4411">
                Confirm that this invoice has been paid
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-4412">
              <CheckCircleIcon size={20} data-id="element-4413" />
              Mark as Paid
            </button>
          </div>
        </div>}
      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4414">
          <div className="flex items-center justify-between" data-id="element-4415">
            <div data-id="element-4416">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-4417">
                Cancel Invoice
              </h3>
              <p className="text-sm text-gray-600" data-id="element-4418">
                This action cannot be undone
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-4419">
              <XCircleIcon size={20} data-id="element-4420" />
              Cancel Invoice
            </button>
          </div>
        </div>}
    </div>;
}